import { useState } from "react";
import { ExpiryBanner } from "@/components/ExpiryBanner";
import { OfflineIndicator } from "@/components/OfflineIndicator";
import { NotificationBell } from "@/components/Notifications";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { ChevronsLeft, ChevronsRight, LogOut, Menu, MoreHorizontal } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABEL } from "@/lib/api";
import { navFor, mobileNavFor } from "./nav";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";

const NavList = ({ groups, collapsed, onNavigate }) => (
  <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-6">
    {groups.map((g) => (
      <div key={g.group}>
        {!collapsed && <p className="px-3 mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">{g.group}</p>}
        <div className="space-y-1">
          {g.items.map((i) => (
            <NavLink key={i.to} to={i.to} end={i.end} onClick={onNavigate} title={i.label}
              data-testid={`nav-${i.to.replace(/\//g, "-").slice(1)}`}
              className={({ isActive }) => cn("pf-nav-item flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium",
                isActive ? "bg-white/10 text-white shadow-[inset_3px_0_0_hsl(var(--gold))]" : "text-slate-300 hover:bg-white/5 hover:text-white",
                collapsed && "justify-center px-0")}>
              <i.icon size={18} className="shrink-0" />
              {!collapsed && <span className="truncate">{i.label}</span>}
            </NavLink>
          ))}
        </div>
      </div>
    ))}
  </nav>
);

const Brand = ({ tenant, collapsed }) => (
  <div className={cn("flex items-center gap-3 px-5 h-16 border-b border-white/10", collapsed && "justify-center px-0")}>
    <div className="h-9 w-9 rounded-lg bg-white grid place-items-center overflow-hidden shrink-0">
      {tenant?.branding?.logo_url ? <img src={tenant.branding.logo_url} alt="" className="h-8 w-8 object-contain" /> : <span className="font-bold text-primary">P</span>}
    </div>
    {!collapsed && <div className="min-w-0">
      <p className="text-white font-display font-bold text-sm truncate" data-testid="sidebar-tenant-name">{tenant?.branding?.name || "PharmaFlow Ghana"}</p>
      <p className="text-[11px] text-slate-400">Pharmacy management</p>
    </div>}
  </div>
);

function TopBar({ onMenu }) {
  const { user, tenant, branches, branch, switchBranch, logout } = useAuth();
  const nav = useNavigate();
  return (
    <header className="sticky top-0 z-30 h-16 bg-white/90 backdrop-blur border-b flex items-center gap-3 px-4 lg:px-8 no-print">
      <button className="lg:hidden h-10 w-10 grid place-items-center rounded-lg hover:bg-muted" onClick={onMenu} data-testid="mobile-menu-button"><Menu size={20} /></button>
      <div className="flex-1 min-w-0 flex items-center gap-3">
        {branches.length > 0 && (
          <Select value={branch} onValueChange={(v) => { switchBranch(v); window.location.reload(); }}>
            <SelectTrigger className="w-auto max-w-[220px] h-9 border-dashed" data-testid="branch-switcher"><SelectValue placeholder="Branch" /></SelectTrigger>
            <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {!tenant && <span className="text-sm font-semibold">Platform administration</span>}
      </div>
      <NotificationBell />
      <DropdownMenu>
        <DropdownMenuTrigger className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-muted" data-testid="user-menu">
          <span className="h-8 w-8 rounded-full bg-slate-900 text-white grid place-items-center text-xs font-bold">{user?.name?.[0]}</span>
          <span className="hidden sm:block text-left leading-tight">
            <span className="block text-sm font-semibold">{user?.name}</span>
            <span className="block text-[11px] text-muted-foreground" data-testid="user-role">{ROLE_LABEL[user?.role]}</span>
          </span>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel className="text-xs">{user?.email}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={async () => { await logout(); nav("/login"); }} data-testid="logout-button"><LogOut size={14} className="mr-2" />Sign out</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}

export default function AppShell() {
  const { can, tenant, user } = useAuth();
  const [collapsed, setCollapsed] = useState(localStorage.getItem("pf_collapsed") === "1");
  const [open, setOpen] = useState(false);
  const groups = navFor(can, user?.role);
  const mobile = mobileNavFor(can);
  const toggle = () => { localStorage.setItem("pf_collapsed", collapsed ? "0" : "1"); setCollapsed(!collapsed); };
  return (
    <div className="min-h-screen flex bg-background">
      <aside className={cn("hidden lg:flex flex-col sticky top-0 h-screen bg-[hsl(var(--navy))] transition-[width] duration-200 no-print", collapsed ? "w-[76px]" : "w-[264px]")} data-testid="sidebar">
        <Brand tenant={tenant} collapsed={collapsed} />
        <NavList groups={groups} collapsed={collapsed} />
        <button onClick={toggle} className="m-3 h-10 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 grid place-items-center" data-testid="sidebar-collapse-toggle">
          {collapsed ? <ChevronsRight size={18} /> : <ChevronsLeft size={18} />}
        </button>
      </aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="p-0 w-[280px] bg-[hsl(var(--navy))] border-0 flex flex-col">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <Brand tenant={tenant} />
          <NavList groups={groups} onNavigate={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
      <div className="flex-1 min-w-0 flex flex-col">
        <TopBar onMenu={() => setOpen(true)} />
        <OfflineIndicator />
        <ExpiryBanner />
        <main className="flex-1 px-4 sm:px-6 lg:px-10 py-8 pb-28 lg:pb-10 max-w-[1600px] w-full mx-auto"><Outlet /></main>
      </div>
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t grid grid-cols-5 no-print" data-testid="mobile-bottom-nav">
        {mobile.map((i) => (
          <NavLink key={i.to} to={i.to} end={i.end} className={({ isActive }) => cn("flex flex-col items-center justify-center gap-1 h-16 text-[11px] font-medium", isActive ? "text-primary" : "text-slate-500")}
            data-testid={`mobile-nav-${i.to.replace(/\//g, "-").slice(1)}`}>
            <i.icon size={20} /><span className="truncate max-w-[70px]">{i.label.split(" ")[0]}</span>
          </NavLink>
        ))}
        <button onClick={() => setOpen(true)} className="flex flex-col items-center justify-center gap-1 h-16 text-[11px] font-medium text-slate-500" data-testid="mobile-nav-more">
          <MoreHorizontal size={20} /><span>More</span>
        </button>
      </nav>
    </div>
  );
}
